import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Card,
  Input,
  Button,
  Table,
  Tag,
  Space,
  message,
  Tooltip,
  Popconfirm,
  Alert,
  Progress,
  Select,
  Row,
  Col,
  Modal,
  DatePicker,
  Typography,
} from "antd";
import {
  PlayCircleOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  FileSearchOutlined,
  FilterOutlined,
  FileAddOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  preRegistrarOcr,
  procesarUnoOcr,
  procesarVariosOcr,
  listarOcrEntregas,
  listarOcrEntregasDetallado,
  eliminarOcrEntrega,
  eliminarVariasOcrEntregas,
  urlVerSoporte,
  obtenerUsoVision,
  listarProyectosOcr,
} from "../../services/ocrActas/OcrEntregasService";
import ActaFormModal from "./ActaFormModal";
import { exportOcrEntregasExcel } from "./exportOcrEntregasExcel";
import { BANDERAS } from "./ocrEntregasBanderas";
import { acortarNombreArchivo, formatMoneda } from "./ocrEntregasUtils";

const { TextArea } = Input;

const STORAGE_KEY_PROYECTO = "ocrActas_oidProyecto";

const ESTADOS_PROCESO = {
  pendiente_proceso: { color: "default", label: "Pendiente de proceso" },
  procesado: { color: "green", label: "Procesado" },
  error_procesamiento: { color: "red", label: "Error al procesar" },
};

// Pequeña etiqueta gris sobre cualquier control de filtro/formulario, para
// que cada campo se entienda de un vistazo sin tener que adivinar.
const CampoEtiquetado = ({ label, children }) => (
  <div>
    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
      {label}
    </Typography.Text>
    {children}
  </div>
);

const OcrEntregasPanel = () => {
  const appName = "informe-ocr-actas";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionDelete = permissions.priv_delete === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionInsert = permissions.priv_insert === "Y";
  const permissionPrint = permissions.priv_print === "Y";
  const permissionExport = permissions.priv_export === "Y";

  const [rutasTexto, setRutasTexto] = useState("");
  const [oidProyecto, setOidProyecto] = useState(() => {
    const guardado = localStorage.getItem(STORAGE_KEY_PROYECTO);
    return guardado ? Number(guardado) : null;
  });
  const [registrando, setRegistrando] = useState(false);
  const [cargandoTabla, setCargandoTabla] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [procesandoOid, setProcesandoOid] = useState(null);
  const [procesandoVarios, setProcesandoVarios] = useState(false);
  const [eliminandoVarios, setEliminandoVarios] = useState(false);
  const [proveedorOcr, setProveedorOcr] = useState("vision");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [datos, setDatos] = useState([]);
  const [resumen, setResumen] = useState(null);

  // Cola de copia por referente: { [oidReferente]: number[] }
  // Persiste mientras la sesion este abierta; se pierde al recargar la pagina.
  const [colaCopia, setColaCopia] = useState({});
  const destinosEnCola = useMemo(
    () => new Set(Object.values(colaCopia).flat()),
    [colaCopia]
  );
  const [oidEditar, setOidEditar] = useState(null);
  const [crearManualAbierto, setCrearManualAbierto] = useState(false);
  const [usoVision, setUsoVision] = useState(null);
  const [proyectos, setProyectos] = useState([]);

  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroEstadoProceso, setFiltroEstadoProceso] = useState("todos");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroBanderas, setFiltroBanderas] = useState("todos");
  const [filtroFechaArchivo, setFiltroFechaArchivo] = useState(null);
  const [filtroProducto, setFiltroProducto] = useState("");
  const [filtroPO, setFiltroPO] = useState("");
  const [filtroArchivo, setFiltroArchivo] = useState("");
  const [filtroIdentificacion, setFiltroIdentificacion] = useState("");

  useEffect(() => {
    listarProyectosOcr()
      .then((respuesta) => setProyectos(respuesta.data || []))
      .catch(() => message.error("No se pudo cargar la lista de proyectos"));
  }, []);

  const cargarUsoVision = useCallback(async () => {
    try {
      const estado = await obtenerUsoVision();
      setUsoVision(estado);
    } catch {
      // El banner de cuota es informativo; si falla no bloqueamos el resto de la pantalla.
    }
  }, []);

  useEffect(() => {
    cargarUsoVision();
  }, [cargarUsoVision]);

  const cargarTabla = useCallback(async (proyecto) => {
    if (!proyecto) return;
    setCargandoTabla(true);
    try {
      const respuesta = await listarOcrEntregas(proyecto);
      setDatos(respuesta.data || []);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al cargar los registros de actas");
    } finally {
      setCargandoTabla(false);
    }
  }, []);

  const handleCambiarProyecto = (valor) => {
    setOidProyecto(valor);
    if (valor) {
      localStorage.setItem(STORAGE_KEY_PROYECTO, String(valor));
    } else {
      localStorage.removeItem(STORAGE_KEY_PROYECTO);
    }
  };

  // Si ya hay un proyecto guardado de una sesion anterior, se carga la tabla
  // automaticamente para no mostrar la pantalla vacia sin explicacion.
  useEffect(() => {
    if (oidProyecto) {
      cargarTabla(oidProyecto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ejecutarRegistro = async (rutas, proyecto, confirmarReproceso) => {
    setRegistrando(true);
    try {
      const respuesta = await preRegistrarOcr(rutas, proyecto, confirmarReproceso);
      setResumen(respuesta);
      message.success(
        `Registrados ${respuesta.registrados} archivo(s) nuevo(s)` +
          (respuesta.reregistrados > 0 ? ` · ${respuesta.reregistrados} re-registrado(s)` : "")
      );
      if (respuesta.rutas_invalidas?.length > 0) {
        message.warning(`${respuesta.rutas_invalidas.length} ruta(s) no se pudieron resolver`);
      }
      await cargarTabla(proyecto);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al registrar los archivos");
    } finally {
      setRegistrando(false);
    }
  };

  const handleRegistrar = async () => {
    if (!permissionInsert) {
      message.warning("No tiene permisos para registrar actas");
      return;
    }

    const rutas = rutasTexto
      .split("\n")
      .map((r) => r.trim())
      .filter(Boolean);

    if (rutas.length === 0 || !oidProyecto) {
      message.warning("Debe indicar al menos una ruta y el proyecto");
      return;
    }

    setRegistrando(true);
    try {
      // Primera llamada sin confirmar: el backend avisa si algun archivo ya
      // fue registrado antes, sin tocar nada todavia.
      const respuesta = await preRegistrarOcr(rutas, oidProyecto, false);

      if (respuesta.requiereConfirmacion) {
        setRegistrando(false);
        Modal.confirm({
          title: "Archivos ya registrados detectados",
          width: 520,
          content: (
            <div>
              <p>
                <b>{respuesta.archivos_duplicados.length}</b> de <b>{respuesta.total_encontrados}</b> archivo(s) ya
                tienen registro en el sistema. Las actas deben ser únicas: si continúa, esos registros (y sus
                productos, si ya estaban procesados) se <b>eliminarán (quedando en el histórico)</b> y se volverán a
                registrar desde cero como pendientes de proceso.
              </p>
              <p style={{ color: "#888", fontSize: 12, marginBottom: 4 }}>Archivos ya registrados:</p>
              <ul style={{ maxHeight: 160, overflowY: "auto", paddingLeft: 18 }}>
                {respuesta.archivos_duplicados.map((nombre) => (
                  <li key={nombre}>{acortarNombreArchivo(nombre)}</li>
                ))}
              </ul>
              {respuesta.archivos_nuevos > 0 && (
                <p style={{ marginTop: 8 }}>
                  Los otros <b>{respuesta.archivos_nuevos}</b> archivo(s) son nuevos y se registrarán normalmente.
                </p>
              )}
            </div>
          ),
          okText: "Eliminar y re-registrar",
          okButtonProps: { danger: true },
          cancelText: "Cancelar",
          onOk: () => ejecutarRegistro(rutas, oidProyecto, true),
        });
        return;
      }

      setResumen(respuesta);
      message.success(`Registrados ${respuesta.registrados} archivo(s) nuevo(s)`);
      if (respuesta.rutas_invalidas?.length > 0) {
        message.warning(`${respuesta.rutas_invalidas.length} ruta(s) no se pudieron resolver`);
      }
      await cargarTabla(oidProyecto);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al registrar los archivos");
    } finally {
      setRegistrando(false);
    }
  };

  const handleEliminar = async (oid) => {
    if (!permissionDelete) {
      message.warning("No tiene permisos para eliminar");
      return;
    }

    try {
      await eliminarOcrEntrega(oid);
      message.success("Acta eliminada (el PDF original no se modifica)");
      await cargarTabla(oidProyecto);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al eliminar el acta");
    }
  };

  const handleProcesarUno = async (oid) => {
    if (!permissionInsert) {
      message.warning("No tiene permisos para procesar actas");
      return;
    }

    setProcesandoOid(oid);
    try {
      await procesarUnoOcr(oid, proveedorOcr);
      message.success("Acta procesada correctamente");
      await cargarTabla(oidProyecto);
      await cargarUsoVision();
    } catch (error) {
      message.error(error.response?.data?.error || "Error al procesar el acta");
    } finally {
      setProcesandoOid(null);
    }
  };

  const handleProcesarVarios = async () => {
    if (!permissionInsert) {
      message.warning("No tiene permisos para procesar actas");
      return;
    }
    if (selectedRowKeys.length === 0) return;

    setProcesandoVarios(true);
    try {
      const respuesta = await procesarVariosOcr(selectedRowKeys, proveedorOcr);
      message.success(`Procesadas ${respuesta.procesados.length} de ${selectedRowKeys.length} acta(s)`);
      if (respuesta.cupo_agotado) {
        message.warning(
          `Se agotó la cuota de Vision: quedaron ${respuesta.pendientes.length} acta(s) sin procesar para el próximo mes.`
        );
      }
      if (respuesta.con_error.length > 0) {
        message.warning(`${respuesta.con_error.length} acta(s) tuvieron error al procesar`);
      }
      await cargarTabla(oidProyecto);
      await cargarUsoVision();
    } catch (error) {
      message.error(error.response?.data?.error || "Error al procesar las actas seleccionadas");
    } finally {
      setProcesandoVarios(false);
    }
  };

  const handleEliminarVarios = async () => {
    if (!permissionDelete) {
      message.warning("No tiene permisos para eliminar");
      return;
    }
    if (selectedRowKeys.length === 0) return;

    setEliminandoVarios(true);
    try {
      const respuesta = await eliminarVariasOcrEntregas(selectedRowKeys);
      message.success(`Eliminadas ${respuesta.eliminados.length} de ${selectedRowKeys.length} acta(s)`);
      if (respuesta.con_error.length > 0) {
        message.warning(`${respuesta.con_error.length} acta(s) tuvieron error al eliminar`);
      }
      setSelectedRowKeys([]);
      await cargarTabla(oidProyecto);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al eliminar las actas seleccionadas");
    } finally {
      setEliminandoVarios(false);
    }
  };

  const datosFiltrados = useMemo(() => {
    const textoCoincide = (valor, busqueda) =>
      !busqueda.trim() || (valor && String(valor).toLowerCase().includes(busqueda.trim().toLowerCase()));

    return datos.filter((fila) => {
      if (filtroTipo !== "todos" && fila.tipo_acta !== filtroTipo) return false;
      if (filtroEstadoProceso !== "todos" && fila.estado_procesamiento !== filtroEstadoProceso) return false;
      if (filtroEstado !== "todos" && fila.estado_validacion !== filtroEstado) return false;

      const tieneBanderas = BANDERAS.some((b) => fila[b.key]);
      if (filtroBanderas === "con" && !tieneBanderas) return false;
      if (filtroBanderas === "sin" && tieneBanderas) return false;
      // Si el valor seleccionado es la clave de una bandera especifica (ej.
      // "bandera_stock_insuficiente"), filtra solo por esa, para poder
      // aislar rapido un tipo de problema en particular.
      if (
        filtroBanderas !== "todos" &&
        filtroBanderas !== "con" &&
        filtroBanderas !== "sin" &&
        !fila[filtroBanderas]
      ) {
        return false;
      }

      if (filtroFechaArchivo) {
        const fechaFila = fila.fecha_archivo ? String(fila.fecha_archivo).split("T")[0] : null;
        if (fechaFila !== filtroFechaArchivo) return false;
      }

      if (!textoCoincide(fila.productos_resumen, filtroProducto)) return false;
      if (!textoCoincide(fila.po_resumen, filtroPO)) return false;
      if (!textoCoincide(fila.nombre_archivo, filtroArchivo)) return false;

      if (
        !textoCoincide(fila.cedula_participante_ocr, filtroIdentificacion) &&
        !textoCoincide(fila.nit_organizacion_ocr, filtroIdentificacion)
      ) {
        return false;
      }

      return true;
    });
  }, [
    datos,
    filtroTipo,
    filtroEstadoProceso,
    filtroEstado,
    filtroBanderas,
    filtroFechaArchivo,
    filtroProducto,
    filtroPO,
    filtroArchivo,
    filtroIdentificacion,
  ]);

  const exportarExcel = async () => {
    if (!permissionExport) {
      message.warning("No tiene permisos para exportar");
      return;
    }
    if (!oidProyecto) {
      message.warning("Seleccione un proyecto primero");
      return;
    }

    setExportando(true);
    try {
      const respuesta = await listarOcrEntregasDetallado(oidProyecto);
      const filas = respuesta.data || [];
      if (filas.length === 0) {
        message.warning("No hay datos para exportar");
        return;
      }
      await exportOcrEntregasExcel(filas);
      message.success(`Se exportaron ${filas.length} registros correctamente`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      message.error("Error al exportar el archivo Excel");
    } finally {
      setExportando(false);
    }
  };

  const handleAgregarACola = useCallback((oidReferente, oidDestino) => {
    setColaCopia((prev) => {
      // Si ya estaba en otra cola, lo saca primero
      const siguiente = Object.fromEntries(
        Object.entries(prev).map(([ref, oids]) => [ref, oids.filter((o) => o !== oidDestino)])
      );
      return { ...siguiente, [oidReferente]: [...(siguiente[oidReferente] || []), oidDestino] };
    });
  }, []);

  const handleQuitarDeCola = useCallback((oidReferente, oidDestino) => {
    setColaCopia((prev) => ({
      ...prev,
      [oidReferente]: (prev[oidReferente] || []).filter((o) => o !== oidDestino),
    }));
  }, []);

  // Llamado desde el modal del referente despues de procesar con exito
  const handleLimpiarCola = useCallback((oidReferente) => {
    setColaCopia((prev) => {
      const siguiente = { ...prev };
      delete siguiente[oidReferente];
      return siguiente;
    });
  }, []);

  const columns = [
    {
      title: "#",
      dataIndex: "oid",
      key: "oid",
      width: 70,
      fixed: "left",
      sorter: (a, b) => a.oid - b.oid,
      defaultSortOrder: "descend",
      render: (v) => (
        <Space direction="vertical" size={2} align="center" style={{ width: "100%" }}>
          <Tag>{v}</Tag>
          {(colaCopia[v] || []).length > 0 && (
            <Tooltip title={`Referente con ${colaCopia[v].length} acta(s) en cola`}>
              <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                Ref ({colaCopia[v].length})
              </Tag>
            </Tooltip>
          )}
          {destinosEnCola.has(v) && (
            <Tooltip title="En cola de copia">
              <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>
                En cola
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Archivo",
      dataIndex: "nombre_archivo",
      key: "nombre_archivo",
      width: 200,
      ellipsis: true,
      render: (nombre, fila) =>
        nombre ? (
          <Tooltip title={nombre}>
            <span>{acortarNombreArchivo(nombre)}</span>
          </Tooltip>
        ) : (
          <Tag color="cyan">Acta manual</Tag>
        ),
    },
    { title: "Carpeta", dataIndex: "carpeta_mes", key: "carpeta_mes", width: 140, render: (v) => v || "-" },
    {
      title: "Tipo",
      dataIndex: "tipo_acta",
      key: "tipo_acta",
      width: 110,
      render: (v) => (
        <Tag color={v === "participante" ? "blue" : v === "organizacion" ? "purple" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "Estado proceso",
      dataIndex: "estado_procesamiento",
      key: "estado_procesamiento",
      width: 150,
      render: (v) => <Tag color={ESTADOS_PROCESO[v]?.color}>{ESTADOS_PROCESO[v]?.label || v}</Tag>,
    },
    {
      title: "Proveedor OCR",
      dataIndex: "proveedor_ocr",
      key: "proveedor_ocr",
      width: 120,
      render: (v) =>
        v === "document_ai" ? (
          <Tag color="purple">Document AI</Tag>
        ) : (
          <Tag color="blue">Vision</Tag>
        ),
    },
    {
      title: "Beneficiario (OCR / BD)",
      key: "beneficiario",
      width: 230,
      render: (_, fila) => {
        const ocr = fila.nombre_participante_ocr || fila.nombre_organizacion_ocr || "-";
        const bd = fila.nombre_participante_bd || fila.nombre_organizacion_bd || "-";
        return (
          <span>
            {ocr}
            {bd !== "-" && bd !== ocr ? <div style={{ color: "#888", fontSize: 12 }}>BD: {bd}</div> : null}
          </span>
        );
      },
    },
    {
      title: "Productos",
      key: "productos",
      width: 160,
      render: (_, fila) => (
        <span>
          <b>{fila.total_productos || 0}</b> producto(s)
          <div style={{ color: "#888", fontSize: 12 }}>{formatMoneda(fila.valor_total_acta)}</div>
        </span>
      ),
    },
    {
      title: "Estado validación",
      dataIndex: "estado_validacion",
      key: "estado_validacion",
      width: 130,
      render: (v) => <Tag color={v === "validado" ? "green" : v === "rechazado" ? "red" : "orange"}>{v}</Tag>,
    },
    {
      title: "Banderas",
      key: "banderas",
      width: 200,
      render: (_, fila) => {
        const activas = BANDERAS.filter((b) => fila[b.key]);
        if (activas.length === 0) return <Tag color="green">Sin banderas</Tag>;
        return (
          <Space size={[0, 4]} wrap>
            {activas.map((b) => (
              <Tooltip key={b.key} title={b.descripcion}>
                <Tag color="red" style={{ marginRight: 0 }}>
                  {b.label}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 220,
      fixed: "right",
      render: (_, fila) => (
        <Space>
          {fila.ruta_acta && permissionPrint && (
            <Tooltip title="Ver soporte">
              <Button
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => window.open(urlVerSoporte(fila.oid), "_blank")}
              />
            </Tooltip>
          )}
          {permissionUpdate && (
            <Tooltip title="Editar / validar">
              <Button size="small" icon={<EditOutlined />} onClick={() => setOidEditar(fila.oid)} />
            </Tooltip>
          )}
          {fila.origen === "archivo" && permissionInsert && (
            <Tooltip title={fila.estado_procesamiento === "procesado" ? "Reprocesar acta" : "Procesar acta"}>
              {fila.estado_procesamiento === "procesado" ? (
                <Popconfirm
                  title="¿Reprocesar esta acta? Se vuelve a leer el PDF y se reemplazan los productos detectados."
                  onConfirm={() => handleProcesarUno(fila.oid)}
                >
                  <Button size="small" icon={<SyncOutlined />} loading={procesandoOid === fila.oid} />
                </Popconfirm>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={procesandoOid === fila.oid}
                  onClick={() => handleProcesarUno(fila.oid)}
                />
              )}
            </Tooltip>
          )}
          {permissionDelete && (
            <Tooltip title="Eliminar acta">
              <Popconfirm
                title="¿Eliminar esta acta y todos sus productos? El PDF original no se borra del servidor."
                onConfirm={() => handleEliminar(fila.oid)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const opcionesProyecto = proyectos.map((p) => ({ value: p.oid, label: `${p.codigo} - ${p.proyecto}` }));

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (fila) => ({ disabled: fila.origen !== "archivo" }),
  };

  return (
    <Card title="OCR de Actas de Entrega" style={{ margin: 16 }} styles={{ body: { padding: 20 } }}>
      {usoVision ? (
        <Alert
          style={{ marginBottom: 20 }}
          type={
            usoVision.paginas_restantes === 0 ? "error" : usoVision.paginas_restantes < 100 ? "warning" : "info"
          }
          showIcon
          message={
            <span>
              Uso de Google Vision este mes ({usoVision.mes}): <b>{usoVision.paginas_usadas}</b> /{" "}
              {usoVision.limite_mensual} páginas · quedan <b>{usoVision.paginas_restantes}</b> páginas (≈{" "}
              <b>{usoVision.actas_restantes_estimadas}</b> actas más) · se reinicia el {usoVision.fecha_reinicio}.
              Registrar archivos NO consume esta cuota; solo "Procesar" la consume.
            </span>
          }
          description={
            <Progress
              percent={Math.round((usoVision.paginas_usadas / usoVision.limite_mensual) * 100)}
              size="small"
              status={usoVision.paginas_restantes === 0 ? "exception" : "active"}
              showInfo={false}
            />
          }
        />
      ) : null}

      <Card type="inner" title="Registrar archivos (no consume cuota)" style={{ marginBottom: 20 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={[20, 12]}>
          <Col flex="1 1 420px">
            <CampoEtiquetado label="Rutas a registrar (una por línea, relativas a DocumentosFAO)">
              <TextArea
                placeholder={
                  "Puede ser un archivo, varios archivos o una carpeta completa, por ejemplo:\n" +
                  "DocumentosPOA/.../Noviembre-Diciembre-2024\n" +
                  "DocumentosPOA/.../archivo_especifico.pdf"
                }
                rows={4}
                value={rutasTexto}
                onChange={(e) => setRutasTexto(e.target.value)}
              />
            </CampoEtiquetado>
          </Col>
          <Col flex="0 0 260px">
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <CampoEtiquetado label="Proyecto">
                <Select
                  showSearch
                  allowClear
                  placeholder="Seleccione un proyecto"
                  style={{ width: "100%" }}
                  value={oidProyecto}
                  onChange={handleCambiarProyecto}
                  options={opcionesProyecto}
                  filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                />
              </CampoEtiquetado>
              {permissionInsert && (
                <Button
                  type="primary"
                  icon={<FileAddOutlined />}
                  loading={registrando}
                  onClick={handleRegistrar}
                  block
                >
                  Registrar
                </Button>
              )}
              {permissionInsert && (
                <Button icon={<PlusOutlined />} onClick={() => setCrearManualAbierto(true)} block disabled={!oidProyecto}>
                  Crear acta manual
                </Button>
              )}
              <Button icon={<ReloadOutlined />} loading={cargandoTabla} onClick={() => cargarTabla(oidProyecto)} block>
                Recargar tabla
              </Button>
              {permissionExport && (
                <Button icon={<DownloadOutlined />} loading={exportando} onClick={exportarExcel} block>
                  Exportar Excel
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {resumen ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 20 }}
          message="Resultado del último registro"
          description={
            <span>
              Total encontrados: {resumen.total_encontrados} · Registrados nuevos: {resumen.registrados} ·
              Re-registrados: {resumen.reregistrados}
              {resumen.rutas_invalidas?.length > 0 ? ` · Rutas inválidas: ${resumen.rutas_invalidas.length}` : ""}
              {resumen.errores?.length > 0 ? ` · Errores: ${resumen.errores.length}` : ""}
            </span>
          }
        />
      ) : null}

      <Card
        type="inner"
        title={
          <Space>
            <FilterOutlined /> Filtros
          </Space>
        }
        style={{ marginBottom: 20 }}
        styles={{ body: { padding: 16 } }}
      >
        <Row gutter={[16, 12]}>
          <Col>
            <CampoEtiquetado label="Tipo">
              <Select
                style={{ width: 160 }}
                value={filtroTipo}
                onChange={setFiltroTipo}
                options={[
                  { value: "todos", label: "Todos" },
                  { value: "participante", label: "Participante" },
                  { value: "organizacion", label: "Organización" },
                ]}
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Estado de proceso">
              <Select
                style={{ width: 180 }}
                value={filtroEstadoProceso}
                onChange={setFiltroEstadoProceso}
                options={[
                  { value: "todos", label: "Todos" },
                  { value: "pendiente_proceso", label: "Pendiente de proceso" },
                  { value: "procesado", label: "Procesado" },
                  { value: "error_procesamiento", label: "Error al procesar" },
                ]}
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Estado de validación">
              <Select
                style={{ width: 170 }}
                value={filtroEstado}
                onChange={setFiltroEstado}
                options={[
                  { value: "todos", label: "Todos" },
                  { value: "pendiente", label: "Pendiente" },
                  { value: "validado", label: "Validado" },
                  { value: "rechazado", label: "Rechazado" },
                ]}
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Banderas">
              <Select
                style={{ width: 220 }}
                value={filtroBanderas}
                onChange={setFiltroBanderas}
                options={[
                  { value: "todos", label: "Todas" },
                  { value: "con", label: "Con cualquier bandera" },
                  { value: "sin", label: "Sin banderas" },
                  ...BANDERAS.map((b) => ({ value: b.key, label: b.label })),
                ]}
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Fecha del archivo">
              <DatePicker
                style={{ width: 160 }}
                value={filtroFechaArchivo ? dayjs(filtroFechaArchivo) : null}
                onChange={(fecha) => setFiltroFechaArchivo(fecha ? fecha.format("YYYY-MM-DD") : null)}
                format="DD/MM/YYYY"
                allowClear
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Nombre del producto">
              <Input
                style={{ width: 200 }}
                placeholder="Ej. Chinchorro"
                value={filtroProducto}
                onChange={(e) => setFiltroProducto(e.target.value)}
                allowClear
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="N° PO">
              <Input
                style={{ width: 140 }}
                placeholder="Ej. 2312207"
                value={filtroPO}
                onChange={(e) => setFiltroPO(e.target.value)}
                allowClear
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Nombre del archivo">
              <Input
                style={{ width: 200 }}
                placeholder="Ej. C96353876"
                value={filtroArchivo}
                onChange={(e) => setFiltroArchivo(e.target.value)}
                allowClear
              />
            </CampoEtiquetado>
          </Col>
          <Col>
            <CampoEtiquetado label="Cédula o NIT">
              <Input
                style={{ width: 170 }}
                placeholder="Ej. 96353876"
                value={filtroIdentificacion}
                onChange={(e) => setFiltroIdentificacion(e.target.value)}
                allowClear
              />
            </CampoEtiquetado>
          </Col>
        </Row>
        <Typography.Text type="secondary" style={{ display: "block", marginTop: 12 }}>
          Mostrando {datosFiltrados.length} de {datos.length} registros
        </Typography.Text>
      </Card>

      {(permissionInsert || permissionDelete) && (
        <Space style={{ marginBottom: 12 }} wrap>
          {permissionInsert && (
            <>
              <Tooltip title="Proveedor de OCR a usar para procesar/reprocesar (permite comparar precisión entre los dos)">
                <Select
                  value={proveedorOcr}
                  onChange={setProveedorOcr}
                  style={{ width: 180 }}
                  options={[
                    { value: "vision", label: "Google Vision" },
                    { value: "document_ai", label: "Document AI" },
                  ]}
                />
              </Tooltip>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                disabled={selectedRowKeys.length === 0}
                loading={procesandoVarios}
                onClick={handleProcesarVarios}
              >
                Procesar seleccionados ({selectedRowKeys.length})
              </Button>
            </>
          )}
          {permissionDelete && (
            <Popconfirm
              title={`¿Eliminar ${selectedRowKeys.length} acta(s) seleccionada(s)?`}
              description="El PDF original no se modifica; quedará registrado en el histórico."
              okText="Eliminar"
              okButtonProps={{ danger: true }}
              onConfirm={handleEliminarVarios}
              disabled={selectedRowKeys.length === 0}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={selectedRowKeys.length === 0}
                loading={eliminandoVarios}
              >
                Eliminar seleccionados ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
        </Space>
      )}

      {Object.keys(colaCopia).filter((k) => colaCopia[k].length > 0).length > 0 && (
        <Alert
          type="info"
          style={{ marginBottom: 12 }}
          message={
            <Space wrap size={[8, 4]}>
              <span>Colas activas:</span>
              {Object.entries(colaCopia)
                .filter(([, oids]) => oids.length > 0)
                .map(([ref, oids]) => {
                  const acta = datos.find((a) => a.oid === Number(ref));
                  const nombre =
                    acta?.nombre_participante_bd ||
                    acta?.nombre_organizacion_bd ||
                    acta?.nombre_participante_ocr ||
                    acta?.nombre_organizacion_ocr ||
                    "";
                  return (
                    <Tag key={ref} color="purple">
                      Ref #{ref}{nombre ? ` — ${nombre}` : ""}: {oids.length} acta(s)
                    </Tag>
                  );
                })}
            </Space>
          }
        />
      )}

      <Table
        rowKey="oid"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={datosFiltrados}
        loading={cargandoTabla}
        scroll={{ x: 1970 }}
        size="middle"
        bordered
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{
          emptyText: oidProyecto
            ? "No hay actas que coincidan con los filtros actuales"
            : "Indique un proyecto y presione \"Recargar tabla\" para ver los registros",
        }}
      />

      <ActaFormModal
        open={!!oidEditar}
        oid={oidEditar}
        oidProyecto={oidProyecto}
        permissionUpdate={permissionUpdate}
        permissionDelete={permissionDelete}
        permissionPrint={permissionPrint}
        onClose={() => setOidEditar(null)}
        onGuardado={() => cargarTabla(oidProyecto)}
        colaCopia={colaCopia}
        todasLasActas={datos}
        onAgregarACola={handleAgregarACola}
        onQuitarDeCola={handleQuitarDeCola}
        onLimpiarCola={handleLimpiarCola}
      />

      <ActaFormModal
        open={crearManualAbierto}
        oid={null}
        oidProyecto={oidProyecto}
        permissionUpdate={permissionUpdate}
        permissionDelete={permissionDelete}
        permissionPrint={permissionPrint}
        onClose={() => setCrearManualAbierto(false)}
        onGuardado={() => cargarTabla(oidProyecto)}
        onCreado={(nuevoOid) => {
          setCrearManualAbierto(false);
          setOidEditar(nuevoOid);
        }}
      />
    </Card>
  );
};

export default OcrEntregasPanel;
